import { useNavigate } from "react-router-dom";
import { Globe2, Moon, Sun, Menu, X } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import PartnerLogos from "./PartnerBar";

export default function TopNavbar() {
  const { theme, toggleTheme } = useTheme();
  const nav = useNavigate();
  const [activePath, setActivePath] = useState("#hero");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["hero", "features", "shipped", "faq"];
      let current = "#hero";
      for (let s of sections) {
        const el = document.getElementById(s);
        if (el && window.scrollY >= (el.offsetTop - 300)) current = "#" + s;
      }
      setActivePath(current);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const NavItem = ({ href, label }) => {
    const isActive = activePath === href;
    const [isHovered, setIsHovered] = useState(false);
    return (
      <a
        href={href}
        onClick={() => setMobileMenuOpen(false)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          textDecoration: "none",
          color: isHovered || isActive ? "var(--text-main)" : "var(--text-muted)",
          fontSize: 14, fontWeight: 600,
          transition: "color 0.2s",
          position: "relative", paddingBottom: 4,
        }}
      >
        {label}
        {isActive && (
          <motion.div layoutId="nav-underline" style={{
            position: "absolute", bottom: -4, left: 0, right: 0,
            height: 2, background: "var(--cyan)", borderRadius: 2,
          }} />
        )}
      </a>
    );
  };

  return (
    <>
      <header className="top-navbar">

        {/* ── GRID COL 1 – LEFT: Hamburger (mobile) + PolicyIQ + partner logos ── */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            className="hamburger-btn"
            onClick={() => setMobileMenuOpen(prev => !prev)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <motion.div
              whileHover={{ rotate: 180 }} transition={{ duration: 0.3 }}
              style={{
                width: 32, height: 32, borderRadius: 8,
                background: "var(--cyan)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Globe2 size={16} color="#000" />
            </motion.div>
            <div style={{ fontWeight: 800, fontSize: 18, color: "var(--text-main)", letterSpacing: "-0.5px" }}>
              PolicyIQ
            </div>
          </div>
          <PartnerLogos />
        </div>

        {/* ── GRID COL 2 – CENTRE: Nav links (hidden on mobile via CSS) ── */}
        <nav className="nav-center">
          <NavItem href="#hero"     label="Home"          />
          <NavItem href="#features" label="Features"      />
          <NavItem href="#shipped"  label="Live Policies" />
          <NavItem href="#faq"      label="FAQ"           />
        </nav>

        {/* ── GRID COL 3 – RIGHT: Actions ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, justifyContent: "flex-end" }}>
          <motion.button
            whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
            onClick={toggleTheme}
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", color: "var(--text-muted)",
            }}
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </motion.button>
          <motion.button
            whileHover={{ backgroundColor: "var(--text-main)", color: "var(--bg-base)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => nav("/login")}
            style={{
              background: "var(--bg-hover)",
              border: "1px solid var(--border)",
              color: "var(--text-main)",
              padding: "8px 16px",
              borderRadius: 8, fontSize: 14, fontWeight: 600,
              cursor: "pointer", transition: "background 0.2s, color 0.2s",
            }}
          >
            Sign In
          </motion.button>
        </div>
      </header>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div style={{
          position: "sticky",
          top: 0,
          zIndex: 49,
          background: "var(--bg-base)",
          borderBottom: "1px solid var(--border)",
          padding: "12px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}>
          <NavItem href="#hero"     label="Home"          />
          <NavItem href="#features" label="Features"      />
          <NavItem href="#shipped"  label="Live Policies" />
          <NavItem href="#faq"      label="FAQ"           />
        </div>
      )}
    </>
  );
}
